import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { payComponentSchema, type PayComponentFormData } from '@/lib/validators';
import { usePayComponents } from '@/composables/payroll/usePayComponents';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  PAY_COMPONENT_CALC_OPTIONS,
  PAY_COMPONENT_TYPE_OPTIONS,
  type PayComponent,
  type PayComponentCalc,
} from '@/types/payroll';

const TYPE_STYLES: Record<string, string> = {
  EARNING: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  DEDUCTION: 'bg-red-500/15 text-red-600 dark:text-red-400',
  EMPLOYER_CONTRIBUTION: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
};

const SECTIONS = [
  { type: 'EARNING' as const, title: 'Earnings', blurb: 'Added together to form gross pay.' },
  { type: 'DEDUCTION' as const, title: 'Deductions', blurb: 'Subtracted from gross to reach net pay.' },
  { type: 'EMPLOYER_CONTRIBUTION' as const, title: 'Employer contributions', blurb: 'Part of CTC and shown on the payslip, but never deducted.' },
];

/** How a value should be labelled and suffixed for a given calc mode. */
function valueMeta(calc: PayComponentCalc): { label: string; suffix: string } | null {
  if (calc === 'BALANCE') return null;
  if (calc === 'FIXED') return { label: 'Amount', suffix: '' };
  return { label: 'Percentage', suffix: '%' };
}

export function PayComponentsPage() {
  const { components, isLoading, createComponent, updateComponent, deleteComponent } = usePayComponents(true);
  const [editing, setEditing] = useState<PayComponent | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PayComponent | null>(null);

  const form = useForm<PayComponentFormData>({
    resolver: zodResolver(payComponentSchema),
    defaultValues: {
      code: '', label: '', type: 'EARNING', calc: 'FIXED', defaultValue: 0,
      isBasic: false, isTaxable: true, isStatutory: false, proratable: true,
      description: '', position: 0, isActive: true,
    },
  });

  const watchCalc = form.watch('calc');
  const meta = valueMeta(watchCalc);

  useEffect(() => {
    if (editing) {
      form.reset({
        code: editing.code, label: editing.label, type: editing.type, calc: editing.calc,
        defaultValue: editing.defaultValue !== null ? Number(editing.defaultValue) : undefined,
        isBasic: editing.isBasic, isTaxable: editing.isTaxable, isStatutory: editing.isStatutory,
        proratable: editing.proratable, description: editing.description ?? '',
        position: editing.position, isActive: editing.isActive,
      });
    } else if (creating) {
      form.reset({
        code: '', label: '', type: 'EARNING', calc: 'FIXED', defaultValue: 0,
        isBasic: false, isTaxable: true, isStatutory: false, proratable: true,
        description: '', position: 0, isActive: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id, creating]);

  const close = () => { setEditing(null); setCreating(false); };

  const onSubmit = (data: PayComponentFormData) => {
    const payload: Record<string, unknown> = { ...data };
    if (data.calc === 'BALANCE') delete payload.defaultValue;
    if (!data.description) delete payload.description;

    if (editing) {
      // `code` is immutable server-side — structures and payslips key off it.
      delete payload.code;
      updateComponent.mutate({ id: editing.id, data: payload }, { onSuccess: close });
    } else {
      createComponent.mutate(payload, { onSuccess: close });
    }
  };

  const isPending = createComponent.isPending || updateComponent.isPending;

  return (
    <PageContainer
      title="Pay Components"
      description="The building blocks every salary structure is assembled from"
      actions={<Button onClick={() => setCreating(true)}><Plus className="mr-2 h-4 w-4" />New Component</Button>}
    >
      <div className="space-y-6">
        {SECTIONS.map((section) => {
          const rows = components.filter((c) => c.type === section.type);
          return (
            <Card key={section.type}>
              <CardHeader>
                <CardTitle className="text-base">{section.title}</CardTitle>
                <p className="text-sm text-muted-foreground">{section.blurb}</p>
              </CardHeader>
              <CardContent>
                {rows.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">None defined.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Calculation</TableHead>
                        <TableHead>Default</TableHead>
                        <TableHead>Flags</TableHead>
                        <TableHead className="w-20" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((c) => (
                        <TableRow key={c.id} className={c.isActive ? undefined : 'opacity-50'}>
                          <TableCell className="font-mono text-xs">{c.code}</TableCell>
                          <TableCell className="font-medium">{c.label}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`border-0 text-xs ${TYPE_STYLES[c.type]}`}>
                              {PAY_COMPONENT_CALC_OPTIONS.find((o) => o.value === c.calc)?.label ?? c.calc}
                            </Badge>
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {c.calc === 'BALANCE' ? '—'
                              : c.calc === 'FIXED' ? Number(c.defaultValue ?? 0).toLocaleString()
                                : `${Number(c.defaultValue ?? 0)}%`}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {c.isBasic && <Badge variant="outline" className="text-[10px]">basic</Badge>}
                              {c.isStatutory && <Badge variant="outline" className="text-[10px]">statutory</Badge>}
                              {!c.proratable && <Badge variant="outline" className="text-[10px]">no proration</Badge>}
                              {!c.isActive && <Badge variant="outline" className="text-[10px]">inactive</Badge>}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(c)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteTarget(c)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          );
        })}
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      </div>

      <Dialog open={creating || !!editing} onOpenChange={(open) => !open && close()}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.label}` : 'New pay component'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'The code cannot be changed — salary structures and issued payslips reference it.'
                : 'Define a line that salary structures can include.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                form={form} name="code" label="Code" required disabled={!!editing}
                placeholder="HRA" description="UPPER_SNAKE_CASE"
              />
              <FormField form={form} name="label" label="Label" required placeholder="House Rent Allowance" />
              <FormSelect form={form} name="type" label="Type" options={PAY_COMPONENT_TYPE_OPTIONS.map((o) => ({ label: o.label, value: o.value }))} />
              <FormSelect form={form} name="calc" label="Calculation" options={PAY_COMPONENT_CALC_OPTIONS.map((o) => ({ label: o.label, value: o.value }))} />
              {meta && (
                <FormField
                  form={form} name="defaultValue" label={`${meta.label}${meta.suffix ? ` (${meta.suffix})` : ''}`}
                  type="decimal" required
                />
              )}
              <FormField form={form} name="position" label="Sort order" type="decimal" />
            </div>

            <p className="text-xs text-muted-foreground">
              {PAY_COMPONENT_CALC_OPTIONS.find((o) => o.value === watchCalc)?.hint}
            </p>

            <FormTextarea form={form} name="description" label="Description" rows={2} />

            <div className="space-y-3 rounded-md border p-3">
              <Toggle form={form} name="isBasic" label="This is the basic component"
                hint="Percent-of-basic components calculate from it. Only one allowed." />
              <Toggle form={form} name="isTaxable" label="Taxable" />
              <Toggle form={form} name="isStatutory" label="Statutory" hint="Marks the line as legally mandated on the payslip." />
              <Toggle form={form} name="proratable" label="Prorate on loss of pay"
                hint="Turn off for flat statutory levies charged in full for any month worked." />
              <Toggle form={form} name="isActive" label="Active" hint="Inactive components stay on existing structures but cannot be added to new ones." />
            </div>

            {form.formState.errors.isBasic && (
              <p className="text-xs text-destructive">{form.formState.errors.isBasic.message as string}</p>
            )}
            {form.formState.errors.calc && (
              <p className="text-xs text-destructive">{form.formState.errors.calc.message as string}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={close}>Cancel</Button>
              <Button type="submit" disabled={isPending}>{isPending ? 'Saving…' : 'Save'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Pay Component"
        description={`"${deleteTarget?.label}" will be removed. If any salary structure uses it, deletion is blocked — deactivate it instead.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteComponent.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteComponent.isPending}
      />
    </PageContainer>
  );
}

function Toggle({
  form, name, label, hint,
}: {
  form: ReturnType<typeof useForm<PayComponentFormData>>;
  name: 'isBasic' | 'isTaxable' | 'isStatutory' | 'proratable' | 'isActive';
  label: string;
  hint?: string;
}) {
  const value = form.watch(name);
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <Label htmlFor={name} className="text-sm">{label}</Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      <Switch id={name} checked={!!value} onCheckedChange={(v) => form.setValue(name, v, { shouldValidate: true })} />
    </div>
  );
}
